// store.ts — typed front-end wrappers over the on-device Rust SQLite commands.
// Every call hits LOCAL SQLite via Tauri IPC (durable, offline, on-disk). Nothing
// here ever leaves the device; the AI brain (brain.ts) is called separately with
// only a small DERIVED-SIGNALS slice computed in Rust (signals.rs).
//
// Data model:
//   plant      { id, name, species, location, potted_on?, created_at, updated_at }
//   care_event { id, plant_id, kind (water|feed|repot|prune|note), done_on, note, created_at }

import { invoke } from "./tauri-web";

/** `milestone` = the moments a diary is actually kept for: new leaf, first flower, propagated. */
export type CareKind = "water" | "feed" | "repot" | "prune" | "mist" | "note" | "milestone" | "photo";

/** One growth measurement. Height and leaf count are each optional; at least one is required. */
export interface GrowthEntry {
  id: string;
  plant_id: string;
  /** "YYYY-MM-DD" */
  measured_on: string;
  height_cm: number | null;
  leaf_count: number | null;
  note: string;
  created_at: string;
}

export interface Plant {
  id: string;
  name: string;
  /** Free-text species/common name, e.g. "Fiddle-leaf fig". "" when unset. */
  species: string;
  /** Free-text location, e.g. "Living room window". "" when unset. */
  location: string;
  /** Date last (re)potted, "YYYY-MM-DD". null when unknown. */
  potted_on: string | null;
  /** Downscaled JPEG data-URL of the plant's photo, ON-DEVICE only. "" when unset. */
  photo_ref: string;
  /** "high"|"medium"|"low" when species came from a photo identification; "" otherwise. */
  species_confidence: string;
  /** The AI-reasoned care profile for THIS plant, as a JSON string (see CareProfile in brain.ts).
   *  "" until the brain sets it. Cached so "what's due today" is instant + free. */
  care_profile: string;
  /** Owner-set health: "thriving" | "ok" | "struggling" | "" (unset). */
  health: string;
  created_at: string;
  updated_at: string;
}

export interface NewPlant {
  name: string;
  species?: string;
  location?: string;
  potted_on?: string | null;
  photo_ref?: string;
  species_confidence?: string;
}
export type PlantPatch = Partial<{
  name: string;
  species: string;
  location: string;
  potted_on: string | null;
  photo_ref: string;
  species_confidence: string;
  care_profile: string;
  health: string;
}>;

/** Result of the device-direct photo → species identification (customer's own key). */
export interface SpeciesId {
  species: string;
  confidence: "high" | "medium" | "low";
}

export interface CareEvent {
  id: string;
  plant_id: string;
  kind: CareKind;
  /** Calendar date the care happened (local tz), "YYYY-MM-DD". */
  done_on: string;
  note: string;
  /** Downscaled JPEG data-URL for THIS moment (the album). "" when none. ON-DEVICE only. */
  photo_ref: string;
  created_at: string;
}
export interface NewCareEvent {
  plant_id: string;
  kind: CareKind;
  done_on?: string;
  note?: string;
  /** Optional photo for this moment (downscaled data-URL). Never leaves the device. */
  photo_ref?: string;
}

export interface NewGrowthEntry {
  plant_id: string;
  measured_on?: string;
  height_cm?: number | null;
  leaf_count?: number | null;
  note?: string;
}

// ── Derived signals (computed on-device in Rust; only aggregates are sent to the brain) ──
// Mirrors src-tauri/src/signals.rs. Every field is a computed aggregate — counts,
// gaps, averages, overdue flags — never the raw timeline. The single exception is
// `latest_note`: the most-recent free-text note (trimmed), so the narration can quote it.
export interface CadenceStat {
  kind: "water" | "feed" | "repot";
  events: number;
  last_on: string | null;
  days_since_last: number | null;
  /** The plant's OWN average gap between consecutive events of this kind (days). */
  avg_interval_days: number | null;
  /** Average of the most-recent gaps (last 3), so the read can show recent vs all-time rhythm. */
  recent_avg_interval_days: number | null;
  /** "tightening" | "widening" | "steady" — recent avg vs all-time avg (observational). */
  trend: "tightening" | "widening" | "steady" | null;
  min_interval_days: number | null;
  max_interval_days: number | null;
  /** days_since_last - avg_interval_days. Positive = past its usual gap. */
  over_under_days: number | null;
  /** True when the current gap exceeds the plant's own average (its personal rhythm). */
  past_personal_rhythm: boolean;
  /** True when this is the plant's largest gap this year for this kind. */
  longest_gap_this_year: boolean;
}
export interface NoteRef {
  on: string;
  kind: string;
  text: string;
}
/** Pre-ranked, ready-to-say observational fact (computed in Rust). Each `text` is a full
 *  sentence already carrying the figure + unit; `tone` is for subtle UI tinting only — NOT
 *  advice. The narration is grounded in these so it never invents a number. */
export type FactTone = "good" | "watch" | "neutral";
export interface HeadlineFact {
  kind: string;
  text: string;
  tone: FactTone;
}
export interface PlantSignals {
  schema: string;
  generated_for: string;
  plant_id: string;
  plant_name: string;
  species: string;
  location: string;
  potted_on: string | null;
  days_since_potted: number | null;
  total_events: number;
  first_event_on: string | null;
  last_event_on: string | null;
  cadence: CadenceStat[];
  latest_note: NoteRef | null;
  headline_facts: HeadlineFact[];
}

export interface PlantWeekRow {
  plant_id: string;
  name: string;
  species: string;
  days_since_water: number | null;
  avg_water_interval_days: number | null;
  water_over_under_days: number | null;
  past_water_rhythm: boolean;
  longest_water_gap_this_year: boolean;
  events_this_week: number;
}
export interface WeekSignals {
  schema: string;
  generated_for: string;
  plant_count: number;
  events_this_week: number;
  plants_past_water_rhythm: number;
  plants: PlantWeekRow[];
  headline_facts: HeadlineFact[];
}

export const store = {
  // Plants
  listPlants: () => invoke<Plant[]>("list_plants"),
  getPlant: (id: string) => invoke<Plant | null>("get_plant", { id }),
  createPlant: (input: NewPlant) => invoke<Plant>("create_plant", { input }),
  updatePlant: (id: string, patch: PlantPatch) => invoke<void>("update_plant", { id, patch }),
  deletePlant: (id: string) => invoke<void>("delete_plant", { id }),
  // Care events
  listEvents: (plantId: string, limit = 50000) => invoke<CareEvent[]>("list_events", { plantId, limit }),
  createEvent: (input: NewCareEvent) => invoke<CareEvent>("create_event", { input }),
  deleteEvent: (id: string) => invoke<void>("delete_event", { id }),

  /** Growth measurements for one plant, OLDEST first (a chart reads left-to-right in time). */
  listGrowth: (plantId: string, limit = 50000) => invoke<GrowthEntry[]>("list_growth", { plantId, limit }),
  /** Rejects an entry with neither a height nor a leaf count (a ghost row helps nobody). */
  createGrowth: (input: NewGrowthEntry) => invoke<GrowthEntry>("create_growth", { input }),
  deleteGrowth: (id: string) => invoke<void>("delete_growth", { id }),
  // Derived signals (on-device math; `today` = client local YYYY-MM-DD so windows align to the user's clock)
  computePlantSignals: (plantId: string, today: string) =>
    invoke<PlantSignals>("compute_plant_signals", { plantId, today }),
  computeWeekSignals: (today: string) => invoke<WeekSignals>("compute_week_signals", { today }),
  // §5d 2026-07-27: speciesKeyPresent / speciesKeySet / identifySpecies /
  // fetchAccountSpeciesKey removed along with the device-direct vendor call they served.
  // The species path is brain.ts::identifySpecies -> POST /identify-species, where the
  // router picks an image-capable model. The SpeciesId type above stays — it is still the
  // shape the brain route returns and what the UI renders.
};

// ── Pure policy / presentation helpers ─────────────────────────────────────────

/** The care actions a user can log, in display order. "note" is a journal entry. */
export const CARE_KINDS: { kind: CareKind; label: string; verb: string; emoji: string }[] = [
  { kind: "water", label: "Watered", verb: "Water", emoji: "💧" },
  { kind: "feed", label: "Fed", verb: "Feed", emoji: "🧪" },
  { kind: "repot", label: "Repotted", verb: "Repot", emoji: "🪴" },
  { kind: "prune", label: "Pruned", verb: "Prune", emoji: "✂️" },
  { kind: "mist", label: "Misted", verb: "Mist", emoji: "💨" },
  { kind: "note", label: "Note", verb: "Note", emoji: "📝" },
  { kind: "milestone", label: "Milestone", verb: "Milestone", emoji: "🌱" },
  { kind: "photo", label: "Photo", verb: "Photo", emoji: "📷" },
];

export function kindLabel(kind: CareKind): string {
  return CARE_KINDS.find((k) => k.kind === kind)?.label ?? kind;
}
export function kindEmoji(kind: CareKind): string {
  return CARE_KINDS.find((k) => k.kind === kind)?.emoji ?? "📝";
}

// ── Local date helpers ─────────────────────────────────────────────────────────
const toLocalDate = (iso: string): Date | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
};
export const formatLocalDate = (d: Date): string =>
  `${d.getFullYear().toString().padStart(4, "0")}-${(d.getMonth() + 1).toString().padStart(2, "0")}-${d.getDate().toString().padStart(2, "0")}`;
export const todayLocal = (): string => formatLocalDate(new Date());

/** Human "3 days ago" / "today" / "yesterday" for a YYYY-MM-DD, relative to today. */
export function daysAgoLabel(dateStr: string, today = new Date()): string {
  const d = toLocalDate(dateStr);
  if (!d) return "";
  const a = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const diff = Math.round((a.getTime() - d.getTime()) / 86400000);
  if (diff <= 0) return "today";
  if (diff === 1) return "yesterday";
  return `${diff} days ago`;
}

/** Pretty short date, e.g. "Mon, Jun 9". Parsed as local. */
export function fmtShortDate(dateStr: string): string {
  const d = toLocalDate(dateStr);
  if (!d) return dateStr;
  return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}

/** A one-line rhythm summary for a plant's watering cadence (deterministic; no AI). */
export function waterRhythmCopy(row: { days_since_water: number | null; avg_water_interval_days: number | null; past_water_rhythm: boolean }): string {
  if (row.days_since_water === null) return "Not watered yet";
  const since = row.days_since_water;
  const sinceTxt = since === 0 ? "watered today" : since === 1 ? "watered yesterday" : `watered ${since} days ago`;
  if (row.avg_water_interval_days === null) return sinceTxt.charAt(0).toUpperCase() + sinceTxt.slice(1);
  const avg = row.avg_water_interval_days;
  if (row.past_water_rhythm) return `Day ${since} · past its usual ~${avg}-day rhythm`;
  return `Day ${since} of its usual ~${avg}-day rhythm`;
}
